import type { FollowModeState, ViewState } from '@annoteer/platform-adapter';
import { isWhiteboard } from './whiteboard.js';

/**
 * Whether a follower should close the document it is showing and go to the
 * whiteboard, because the presenter did.
 *
 * A presenter with no document open broadcasts the whiteboard's id (SidePanel).
 * Until 2026-09-14 closing broadcast nothing, so followers kept the closed
 * document while the presenter drew on the whiteboard -- reported from a phone.
 * Stop presenting still leaves followers where they are (decided 2026-09-02):
 * this only runs while following.
 *
 * `followOverride` means the user chose what they are looking at (opened their
 * own document, or closed the presenter's), so the presenter must not take it
 * away. A blink of the presenter's id to null is not the whiteboard and moves
 * nobody.
 */
export function shouldReturnToWhiteboard(
  presenterDocId: string | null | undefined,
  openDocumentId: string | null | undefined,
  isFollowing: boolean,
  followOverride: boolean,
): boolean {
  if (!isFollowing || followOverride) return false;
  if (!isWhiteboard(presenterDocId)) return false;
  return openDocumentId != null;
}

/**
 * The view state to follow, or null if there is nothing to follow.
 *
 * Live Share's follow state does not always carry the presenter's value. It
 * also delivers the LOCAL user's own view state, flagged by `isLocalValue`.
 * A follower who never presents keeps their initial `{ currentPage: 1 }`
 * forever, so honouring those events drags them to page 1, and the next
 * presenter event drags them back — the page flipping between 1 and the
 * presenter's page every few seconds that was reported on 2026-08-15.
 *
 * Following your own state is never meaningful: either you are the presenter,
 * in which case you already are where you are, or you are a follower and your
 * own value is stale by definition.
 *
 * `isLocalValue` alone is not enough, because a value can be somebody else's
 * and still be a placeholder. Every client calls `initialize()` with
 * `{ documentId: null, currentPage: 1 }` (LiveShareContext), and a client that
 * never presents never replaces it — the presenter's OWN meeting stage is
 * exactly such a client, since the stage deliberately does not broadcast and
 * relays its page over awareness instead. The presenter therefore holds two
 * Live Share connections under one user: the panel, carrying the real view,
 * and the stage, carrying `{ null, 1 }`. Whichever refreshed last can answer
 * for that user, so followers were handed page 1 every few seconds with
 * `isLocalValue: false` — indistinguishable from a real instruction under the
 * old guard, and the flicker reported from a phone on 2026-09-01.
 *
 * A real broadcast always names a document: SidePanel sends the open
 * document's id, or the whiteboard's id when none is open. So a value with no
 * `documentId` has never been written by anybody, and must not move anyone.
 */
export function resolvePresenterView(state: FollowModeState | null): ViewState | null {
  if (!state || state.isLocalValue) return null;
  const value = state.value ?? null;
  if (!value?.documentId) return null;
  return value;
}

/**
 * The page a follower should be moved to, or undefined to stay put.
 * Page numbers are 1-based, so 0 is treated as "no page" rather than a page.
 *
 * `openDocumentId` is the document this client is actually showing. A page
 * number only means anything against the document it was counted in, so a view
 * state for some other document must not move us — it would scroll the reader
 * to a page chosen for a document they are not looking at. Once we catch up to
 * the presenter's document, page-following resumes on its own.
 */
export function resolveFollowPage(
  state: FollowModeState | null,
  isFollowing: boolean,
  isSuspended: boolean,
  openDocumentId?: string | null,
): number | undefined {
  if (!isFollowing && !isSuspended) return undefined;
  const view = resolvePresenterView(state);
  if (!view) return undefined;
  if (openDocumentId != null && view.documentId !== openDocumentId) return undefined;
  const page = view.currentPage;
  return typeof page === 'number' && page > 0 ? page : undefined;
}

/**
 * The presenter page a follower should move to now, or null to do nothing.
 *
 * Following is driven by a React effect, and an effect re-running is NOT the
 * same event as the presenter turning a page: it re-runs whenever any
 * dependency changes. `followPage` in particular blinks to undefined and back
 * on its own, because the presenter's placeholder connection periodically
 * answers for them and resolvePresenterView correctly rejects it. Re-applying
 * the page on every re-run therefore dragged a follower who had turned a page
 * of their own straight back within seconds — reported from a phone on
 * 2026-09-01 as "the phone does not master the page".
 *
 * `honouredPage` is the last page this client acted on. Honouring each
 * instruction exactly once makes the effect idempotent, so only a genuinely
 * new page from the presenter moves anyone — which is what following was
 * always meant to mean, and what lets a follower read ahead until the
 * presenter moves again.
 *
 * A page beyond `numPages` is refused WITHOUT being recorded as honoured:
 * numPages starts at 0 and only becomes real once the PDF parse resolves, so
 * a follower joining onto a presenter already on page 5 must still be moved
 * there once the page count is known.
 */
export function pendingFollowPage(
  followPage: number | undefined,
  honouredPage: number | undefined,
  numPages: number,
): number | null {
  if (followPage == null) return null;
  if (followPage < 1 || followPage > numPages) return null;
  if (followPage === honouredPage) return null;
  return followPage;
}
